import React from 'react';
import classnames from 'classnames';
import IconCancel from '../../../../audi-ui-components/icons/Cancel';
import { markerIcons, mapDefaultSettings, mapDefaultStyle, mapDarkSettings, mapDarkStyle } from './MapOptions';
import {
    DEFAULT_THEME,
} from '../../../../constants';
import queryString from 'query-string';
import { withRouter } from 'react-router-dom';
import { Wrapper, Status } from "@googlemaps/react-wrapper";

class WrappedMap extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            mapLoaded: false,
            focus: true//props.alwaysFocus,
        };
        this.markers = [];
        this.mapRef = React.createRef();
        this.containerRef = React.createRef();
    }

    componentDidMount() {
        // Load google maps
        // GoogleMapsLoader.KEY = this.props.apiKey;
        // GoogleMapsLoader.VERSION = '3.54';
        // GoogleMapsLoader.load(this.onGoogleMapsAPILoad);
        this.onGoogleMapsAPILoad();

        // Bind to all click events to handle focus and blur
        if (window) {
            window.addEventListener('mousedown', this.handleWindowMousedown);
            window.addEventListener('touchstart', this.handleWindowMousedown);
            window.addEventListener('mousemove', this.handleWindowMousemove);
            window.addEventListener('touchmove', this.handleWindowMousemove);
            window.addEventListener('mouseup', this.handleWindowMouseup);
            window.addEventListener('touchend', this.handleWindowMouseup);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // console.log("Map componentDidUpdate");
        if (!this.map) {
            return;
        }
        if (this.props.markers.length !== prevProps.markers.length) {
            // console.log("Map componentDidUpdate markers length changed");
            this.renderMarkers(this.props.markers, this.props.animateMarkers);
        } else if (
            this.props.markers.length &&
            (this.props.markers[0].lat !== prevProps.markers[0].lat || this.props.markers[0].lng !== prevProps.markers[0].lng)
        ) {
            // console.log("Map componentDidUpdate markers[0] changed");
            this.renderMarkers(this.props.markers, this.props.animateMarkers);
        } else if (this.props.selectedId !== prevProps.selectedId) {
            this.renderMarkers(this.props.markers, this.props.animateMarkers);
        }
        if (prevState.focus && !this.state.focus) {
            this.onBlur();
        } else if (!prevState.focus && this.state.focus) {
            this.onFocus();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('mousedown', this.handleWindowMousedown);
        window.removeEventListener('touchstart', this.handleWindowMousedown);
        window.removeEventListener('mousemove', this.handleWindowMousemove);
        window.removeEventListener('touchmove', this.handleWindowMousemove);
        window.removeEventListener('mouseup', this.handleWindowMouseup);
        window.removeEventListener('touchend', this.handleWindowMouseup);
    }

    onGoogleMapsAPILoad = () => {
        this.google = google;

        // Create marker icon styles
        this.markerIcons = {};
        Object.keys(markerIcons).forEach(key => {
            const icon = markerIcons[key];
            const { anchor } = icon;

            this.markerIcons[key] = anchor ? Object.assign({}, icon, {
                anchor: new this.google.maps.Point(anchor.x, anchor.y),
            }) : icon;
        });

        this.renderMap();
        this.renderMarkers(this.props.markers, this.props.animateMarkers);

        // Blur map if necessary
        // This prevents dragging and scrolling until it is next focussed
        if (!this.state.focus) {
            this.onBlur();
        }

        this.setState({
            mapLoaded: true,
        });
    }

    onFocus = () => {
        // Set draggable and scrollwheel to true, or to their values defined in props
        const { mapSettings } = this.props;
        this.map.setOptions({
            draggable: mapSettings && ('draggable' in mapSettings) ?
                            mapSettings.draggable : true,
            scrollwheel: mapSettings && ('scrollwheel' in mapSettings) ?
                            mapSettings.scrollwheel : true,
        });
    }

    onBlur = () => {
        // Set draggable and scrollwheel to false
        this.map.setOptions({
            draggable: false,
            scrollwheel: false,
        });
    }

    handleWindowMousedown = (e) => {
        this.isDraggingCursor = false;
        this.mousedownInMap = this.containerRef.current.contains(e.target);
    }

    handleWindowMousemove = () => {
        this.isDraggingCursor = true;
    }

    handleWindowMouseup = () => {
        // Do not gain focus if this is the end of a drag
        if (!this.state.focus && this.isDraggingCursor) {
            return;
        }

        // Focus or blur, depending on whether click was inside or outside map
        this.setState({
            focus: this.props.alwaysFocus || this.mousedownInMap,
        });
    }

    renderMap = () => {
        const query = queryString.parse(this.props.location.search);
        const theme = this.props.theme || query.theme || DEFAULT_THEME;
        const { mapSettings } = this.props;
        const mapUseSettings = theme === 'dark' ? mapDarkSettings : mapDefaultSettings; 
        const mapUseStyle = theme === 'dark' ? mapDarkStyle : mapDefaultStyle;
        const mapOptions = Object.assign({}, mapUseSettings, mapSettings, {
            center: this.props.defaultCenter,
            zoomControlOptions: {
                style: this.google.maps.ZoomControlStyle.SMALL,
                position: this.google.maps.ControlPosition.RIGHT_TOP,
            },
            styles: mapUseStyle,
        });

        this.map = new this.google.maps.Map(this.mapRef.current, mapOptions);
    }

    renderMarkers = (markers, animate) => {
        // console.log("Map renderMarkers", markers.length);
        // First check if there are any changes
        if ((markers ? markers.length : 0) === this.markers.length) {
            // Same number of markers, check if they have the same latitudes & longitudes
            const changed = this.markers.reduce((p, c, i) => {
                return p || (c.lat !== markers[i].lat || c.lng !== markers[i].lng || c.icon !== markers[i].icon);
            }, false);

            if (!changed) {
                return;
            }
        }

        // Reset existing markers
        this.markers.forEach(marker => marker.googleMarker.setMap(null));

        if (!markers || !markers.length) {
            this.markers = [];
            return;
        }

        const bounds = new this.google.maps.LatLngBounds();

        // Set new markers
        this.markers = markers.map((marker) => {
            bounds.extend(new this.google.maps.LatLng({
                lat: marker.lat,
                lng: marker.lng,
            }));

            const googleMarker = new this.google.maps.Marker({
                map: this.map,
                clickable: !!marker.onClick,
                position: {
                    lat: marker.lat,
                    lng: marker.lng,
                },
                animation: animate ? this.google.maps.Animation.DROP : null,
                icon: this.markerIcons[marker.icon || 'default'],
            });

            if (marker.onClick) {
                googleMarker.addListener('click', marker.onClick);
            }

            return {
                lat: marker.lat,
                lng: marker.lng,
                googleMarker,
            };
        });

        // Fit bounds
        // console.log("Map renderMarkers bounds", bounds);
        let padding = {left: 10, right: 10, top: 10, bottom: 10};
        let w = window.innerWidth;
        if (this.props.padLeft && w >= this.props.breakpoint) {
            let frameW = w > 1920 ? 1920 : w;
            padding.left = ((frameW / 100) * 10) + 410;
        }
        if (this.props.padBottom) {
          if (w >= this.props.breakpoint) {
            padding.bottom = 200;
          } else {
            padding.bottom = 360;
          }
        }
        if (markers.length === 1) {
            bounds.extend(new this.google.maps.LatLng({
                lat: markers[0].lat + 0.01,
                lng: markers[0].lng,
            }));
            bounds.extend(new this.google.maps.LatLng({
                lat: markers[0].lat - 0.01,
                lng: markers[0].lng,
            }));
        }
        this.map.fitBounds(bounds, padding);

        // Fix zoom after setting bounds
        // const maxZoom = this.props.maxAutoZoomLevel;
        // if (this.map.getZoom() > maxZoom) this.map.setZoom(maxZoom);

        // Sometimes it will not work immediately - wait until idle and try again, just to be sure
        // const listener = this.google.maps.event.addListener(this.map, 'idle', () => {
        //     if (this.map.getZoom() > maxZoom) this.map.setZoom(maxZoom);
        //     this.google.maps.event.removeListener(listener);
        // });
    }

    render() {
        const classes = classnames(this.props.className, 'audi-map', {
            'audi-map--blur': !this.state.focus,
            'audi-map--focus': this.state.focus,
            'audi-map--show-blur-state': this.props.showBlurState,
        });

        return (
            <div ref={this.containerRef} className={classes}>
                <div ref={this.mapRef} className="audi-map__google"></div>
                {this.props.showClose ? (
                    <button className="audi-map__close" onClick={this.props.onCloseClick}><IconCancel large /><span className="sr-only">Close</span></button>
                ) : ''}
            </div>
        );
    }
}

const Map = (props) => {
  return (
    <Wrapper
      apiKey={process.env.RAZZLE_GOOGLE_MAPS_API_KEY}
      libraries={["places", "geocoding"]}
      render={(status) => {
        if (status === Status.FAILURE) {
          return <div />;
        }
        return null;
      }}
    >
      <WrappedMap {...props} />
    </Wrapper>
  );
};

WrappedMap.defaultProps = {
    animateMarkers: true,
    maxAutoZoomLevel: 15,
    breakpoint: 900
};

export default withRouter(Map);
